import styled, { keyframes, css } from "styled-components";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevronLeft.svg";

/* Subtle glow pulse on the active page button — feels like a lit water pearl */
const activeGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 10px 22px rgba(var(--color-brand-600-rgb), 0.32),
      0 2px 6px rgba(var(--color-brand-600-rgb), 0.16),
      inset 0 1px 0 rgba(255, 255, 255, 0.62),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  }
  50% {
    box-shadow:
      0 14px 28px rgba(var(--color-brand-600-rgb), 0.46),
      0 3px 8px rgba(var(--color-brand-600-rgb), 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.72),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.38);
  }
`;

export const PaginationContainer = styled.ul`
  display: flex;
  align-items: center;
  gap: ${(p) => (p.$small ? "6px" : "10px")};
  list-style: none;
  padding: 0;
  margin: 0;

  @media (max-width: 500px) {
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: 375px) {
    gap: ${(p) => (p.$small ? "4px" : "8px")};
  }
`;

/* Previous / next arrow — frosted-glass circle (matches MenuButton) */
export const ArrowContainer = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => (p.$small ? "32px" : "42px")};
  height: ${(p) => (p.$small ? "32px" : "42px")};
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.72) 0%,
    rgba(var(--glass-white-rgb), 0.46) 45%,
    rgba(var(--glass-pale-rgb), 0.34) 100%
  );
  backdrop-filter: blur(22px) saturate(185%);
  -webkit-backdrop-filter: blur(22px) saturate(185%);
  box-shadow:
    0 8px 20px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -4px 12px rgba(var(--color-brand-200-rgb), 0.22);

  [data-theme="dark"] & {
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -3px 10px rgba(var(--color-brand-700-rgb), 0.16);
  }
  list-style: none;
  cursor: pointer;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy),
    opacity 220ms var(--ease-snappy);

  /* Top-left sheen like a glass pearl */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 18px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover {
    transform: ${({ disabled }) =>
      !disabled ? "translateY(-1px) scale(1.04)" : "none"};
    border-color: ${({ disabled }) =>
      !disabled
        ? "rgba(var(--color-brand-400-rgb), 0.45)"
        : "var(--glass-edge-soft)"};
  }

  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};

  @media (max-width: 375px) {
    width: ${(p) => (p.$small ? "28px" : "36px")};
    height: ${(p) => (p.$small ? "28px" : "36px")};
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ArrowIcon = styled(ChevronIcon)`
  width: ${(p) => (p.$small ? "14px" : "18px")};
  height: ${(p) => (p.$small ? "14px" : "18px")};
  position: relative;
  z-index: 1;

  path {
    stroke: ${(props) =>
      props.$disabled ? "rgba(159, 177, 206, 0.42)" : "var(--color-brand-600)"};
    transition: stroke 180ms var(--ease-snappy);
  }

  [data-theme="dark"] & path {
    stroke: ${(props) =>
      props.$disabled
        ? "rgba(180, 200, 230, 0.32)"
        : "var(--color-text-strong)"};
  }

  @media (max-width: 375px) {
    width: ${(p) => (p.$small ? "12px" : "16px")};
    height: ${(p) => (p.$small ? "12px" : "16px")};
  }
`;

export const RightArrowIcon = styled(ArrowIcon)`
  transform: rotate(180deg);
`;

/* Page number button — water pill. Unselected: frosted-white. Selected: brand
   gradient with pulsing glow. */
export const PaginationItem = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${(p) => (p.$small ? "32px" : "42px")};
  height: ${(p) => (p.$small ? "32px" : "42px")};
  padding: 0 12px;
  border-radius: var(--radius-pill);
  font-family: var(--font-family-primary);
  font-weight: ${({ selected }) => (selected ? 700 : 500)};
  font-size: ${(p) => (p.$small ? "13px" : "14px")};
  letter-spacing: -0.01em;
  list-style: none;
  cursor: ${({ selected }) => (selected ? "default" : "pointer")};
  pointer-events: ${({ selected }) => (selected ? "none" : "auto")};
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    color 220ms var(--ease-snappy),
    filter 220ms var(--ease-snappy);

  /* Variant styling */
  ${({ selected }) =>
    selected
      ? css`
          color: var(--glass-text-on-pill);
          border: 1px solid rgba(var(--glass-white-rgb), 0.72);
          background:
            radial-gradient(
              140% 100% at 20% 0%,
              rgba(255, 255, 255, 0.3) 0%,
              rgba(255, 255, 255, 0) 55%
            ),
            linear-gradient(
              180deg,
              var(--color-brand-400) 0%,
              var(--color-brand-600) 55%,
              var(--color-brand-700) 100%
            );
          animation: ${activeGlow} 3.6s ease-in-out infinite;
          text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.38);

          [data-theme="dark"] & {
            border-color: transparent;
            animation: none;
            border: none;
            box-shadow:
              0 6px 14px rgba(0, 0, 0, 0.5),
              0 2px 6px rgba(0, 0, 0, 0.32),
              inset 0 -3px 6px rgba(0, 0, 0, 0.22);
          }
        `
      : css`
          color: var(--color-brand-700);
          border: 1px solid var(--glass-edge-soft);
          background: radial-gradient(
            120% 100% at 20% 0%,
            rgba(var(--glass-white-rgb), 0.82) 0%,
            rgba(var(--glass-pale-rgb), 0.6) 55%,
            rgba(var(--glass-pale-rgb), 0.48) 100%
          );
          backdrop-filter: blur(18px) saturate(180%);
          -webkit-backdrop-filter: blur(18px) saturate(180%);
          box-shadow:
            0 6px 14px rgba(var(--color-brand-500-rgb), 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.9),
            inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.18);

          [data-theme="dark"] & {
            color: var(--color-text-strong);
            box-shadow:
              0 4px 10px rgba(0, 0, 0, 0.45),
              inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.14);
          }
        `}

  /* Top sheen reflection */
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 12px;
    right: 12px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, ${({ selected }) => (selected ? 0.5 : 0.62)}) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover {
    transform: ${({ selected }) => (selected ? "none" : "translateY(-1px)")};
    filter: ${({ selected }) => (selected ? "brightness(1.05)" : "none")};
    ${({ selected }) =>
      !selected &&
      css`
        border-color: rgba(var(--color-brand-400-rgb), 0.45);
        box-shadow:
          0 10px 20px rgba(var(--color-brand-500-rgb), 0.16),
          inset 0 1px 0 rgba(255, 255, 255, 1),
          inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.28);
      `}
  }

  @media (max-width: 375px) {
    min-width: ${(p) => (p.$small ? "28px" : "36px")};
    height: ${(p) => (p.$small ? "28px" : "36px")};
    font-size: ${(p) => (p.$small ? "12px" : "13px")};
    padding: 0 8px;
  }
`;
